import { useCallback, useMemo } from "react";

import { CheckCircledIcon } from "@radix-ui/react-icons";
import { Flex, Tabs, Text } from "@radix-ui/themes";
import { styled } from "@stitches/react";
import { useSearchParams } from "react-router-dom";

import { ContactRequestListing } from "./components/ContactRequestListing";
import { SampleRequestListing } from "./components/SampleRequestListing";
import { ContactRequest, SampleRequest } from "../../api/public";
import { Callout } from "../../common/components";
import { useTabsStateValue } from "../../hooks/useTabsValue";
import { useSampleRequestContext } from "../../state/SampleRequestContext";
import { NeedsVerifiedUser } from "../components/NeedsVerification";

const TabsRoot = styled(Tabs.Root, {
  display: "flex",
  flexDirection: "column",
  gap: "var(--space-4)",
});

export const SampleRequests = () => {
  const { showSampleRequestDetail, showContactRequestDetail } =
    useSampleRequestContext();
  const [params] = useSearchParams();
  const success = params.get("success") === "true";
  const tabsValue = useTabsStateValue("sample-requests");

  const handleContactRequestRowClick = useCallback(
    (contactRequest: ContactRequest) => {
      showContactRequestDetail(contactRequest);
    },
    [showContactRequestDetail]
  );

  const handleSampleRequestRowClick = useCallback(
    (sampleRequest: SampleRequest) => {
      showSampleRequestDetail(sampleRequest);
    },
    [showSampleRequestDetail]
  );

  const tabs = useMemo(() => {
    return [
      {
        value: "sample-requests",
        label: "Sample Requests",
        Component: (
          <SampleRequestListing onRowClick={handleSampleRequestRowClick} />
        ),
      },
      {
        value: "contact-requests",
        label: "Contact Requests",
        Component: (
          <ContactRequestListing onRowClick={handleContactRequestRowClick} />
        ),
      },
    ];
  }, [handleContactRequestRowClick, handleSampleRequestRowClick]);

  return (
    <NeedsVerifiedUser>
      <Flex direction="column" gap="4">
        {success && (
          <Callout color="green" icon={<CheckCircledIcon color="green" />}>
            <Text size="2" color="green">
              Your sample request has been submitted successfully. You can view
              its details by clicking on the appropriate row in the table below.
            </Text>
          </Callout>
        )}
        <TabsRoot {...tabsValue}>
          <Tabs.List>
            {tabs.map((tab) => (
              <Tabs.Trigger key={tab.value} value={tab.value}>
                {tab.label}
              </Tabs.Trigger>
            ))}
          </Tabs.List>
          {tabs.map(({ value, Component }) => (
            <Tabs.Content
              key={value}
              value={value}
              forceMount
              hidden={value !== tabsValue?.value}
            >
              {Component}
            </Tabs.Content>
          ))}
        </TabsRoot>
      </Flex>
    </NeedsVerifiedUser>
  );
};
